<template>
  <button
    class="profile-button"
    :class="{ open }"
    v-bind="$attrs"
    data-external="topbar-profile-dropdown-button"
  >
    <FriedAvatar
      :size="AvatarSize.Small"
      :name="(user as User).fullName"
      :thumb-url="(user as User).thumbUrl"
      :image-transform="getImageTransform(user)"
    />
    <div class="information hidden-sm-and-down">
      <FriedH5 class="name"> {{ (user as User).firstName || (user as User).email }}</FriedH5>
      <FriedParagraph :size="ParagraphSize.Small" class="entity" :title="entityTitle">
        {{ entityName }}
      </FriedParagraph>
    </div>
    <div class="icon-container hidden-sm-and-down">
      <FriedIcon icon="chevron-down" />
    </div>
  </button>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, computed } from 'vue';
import { AvatarSize, ParagraphSize } from '@getaccept/fried-tofu';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { AvatarHelper } from '@getaccept/lib-shared/src/helpers/avatars.helper';

export default defineComponent({
  name: 'ProfileDropdownButton',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object as PropType<User>,
      required: true,
    },
    entityName: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props) {
    const entityTitle = computed(() => {
      const visibleCharacters = 12;
      return props.entityName?.length > visibleCharacters ? props.entityName : '';
    });

    return {
      getImageTransform: AvatarHelper.getImageTransform,
      AvatarSize,
      ParagraphSize,
      entityTitle,
    };
  },
});
</script>

<style lang="scss" scoped>
$intercom-notification-overlap: 0.375rem;
$margin-left: calc(#{$intercom-notification-overlap} + #{var(--spacing-100)});

.profile-button {
  height: 3.5rem;
  width: 230px;
  border-radius: var(--radius-medium);
  background: var(--white);
  border: none;
  display: flex;
  text-align: left;
  align-items: center;
  padding: 0 var(--spacing-100);
  color: var(--text-gray);
  cursor: pointer;

  &.open,
  &:hover {
    background: var(--gray-96);
  }

  &:active {
    background: var(--gray-94);
  }

  @media only screen and (max-width: calc(#{$sm} - 1px)) {
    width: 2rem;
    padding: 0;

    > * {
      margin: 0;
    }

    &.open,
    &:hover,
    &:active {
      background: var(--white);
    }
  }

  .information {
    flex-grow: 1;
    overflow: hidden;
  }

  .name,
  .entity {
    color: var(--text-gray);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
  }

  .icon-container {
    margin-left: var(--spacing-50);
    display: flex;
    align-items: center;
    transition: all 0.2s ease-out;
  }

  .avatar {
    margin-right: var(--spacing-50);
  }

  &.open .icon-container {
    transform: rotateZ(180deg);
  }
}
</style>
